import { useRef, useState } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { Panel } from "./_shared";
import { Receipt, Eye, FileArrowUp, Trash, ArrowsClockwise } from "@phosphor-icons/react";
import { isCompanyInactive } from "../../../components/RequireSenderCompany";

const TERMINAL = ["DELIVERED", "CANCELLED"];
const ACCEPT = "application/pdf,image/*";

// M10A FE3 — owning-Sender post-booking INVOICE management, inline in the Sender shipment detail.
// Consumes ONLY the Inc-4 sender routes (POST /documents, DELETE .../{id}, POST .../{id}/replace) +
// the existing signed-doc route. NEVER exposes any E-Way Bill data (no number, no per-leg PDFs, no ids):
// it drives the correct approved action from the derived booleans invoice_has_ewb /
// invoice_replacement_pending / invoice_is_pending_replacement returned on the shipment payload.
export function SenderInvoicesPanel({ d, reload, profile }) {
  const addRef = useRef(null);
  const replaceRefs = useRef({});
  const [busy, setBusy] = useState(false);

  if (profile?.role !== "SENDER") return null;

  const shipmentId = d.shipment.id;
  const active = !TERMINAL.includes(d.shipment.current_status);
  const paid = d.shipment.air12_payment_status === "COMPLETED";
  const canModify = active && !paid;
  // A11 — INACTIVE company: no Goods Invoice upload / replace before MAIN (backend enforces too).
  const canUpload = canModify && !isCompanyInactive(profile);
  // Delete only while MAIN is UNLOCKED — once checkout is initiated (PENDING) the last Goods Invoice
  // cannot be deleted (backend 409 GOODS_INVOICE_CHECKOUT_LOCKED remains the authoritative guard).
  const canDelete = active && d.shipment.air12_payment_status === "UNLOCKED";
  const invDocs = (d.documents || []).filter((x) => x.doc_type === "INV");
  const currentInvoices = invDocs.filter((x) => !x.invoice_is_pending_replacement);
  const pendingDocs = invDocs.filter((x) => x.invoice_is_pending_replacement);
  const singlePending = pendingDocs.length === 1 ? pendingDocs[0] : null;

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); }
    catch (e) { toast.error(e.displayMessage || "That didn't go through. Please try again."); }
    finally { setBusy(false); await reload(); }
  };

  const openSigned = async (docId) => {
    try {
      const { data } = await api.get(`/shipments/${shipmentId}/documents/${docId}/signed-url`);
      if (data?.signed_url) window.open(data.signed_url, "_blank", "noopener");
      else toast.error("Document unavailable");
    } catch (e) { toast.error(e.displayMessage || "Could not open document"); }
  };

  const addInvoice = (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    run(async () => {
      const fd = new FormData();
      fd.append("doc_type", "INV");
      fd.append("file", file);
      await api.post(`/shipments/${shipmentId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Invoice added");
    });
  };

  const deleteInvoice = (doc) => {
    if (!window.confirm("Delete this invoice from the shipment? The invoice file will be removed.")) return;
    run(async () => {
      await api.delete(`/shipments/${shipmentId}/documents/${doc.id}`);
      toast.success("Invoice deleted");
    });
  };

  const replaceInvoice = (doc, postEwb) => (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const msg = postEwb
      ? "Submit this replacement invoice for Accounts review? Your current invoice stays in place until Accounts confirms it."
      : "Replace this invoice? The current invoice will be removed and the new one becomes current.";
    if (!window.confirm(msg)) return;
    run(async () => {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/shipments/${shipmentId}/documents/${doc.id}/replace`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data?.mode === "POST_EWB_PENDING" ? "Replacement submitted for Accounts review" : "Invoice replaced");
    });
  };

  return (
    <Panel
      icon={<Receipt size={16} weight="bold" />}
      title={`GOODS INVOICE${currentInvoices.length ? "" : ""}`}
      action={canUpload && currentInvoices.length === 0 && (
        <label className="btn-ghost cursor-pointer" data-testid="sender-add-invoice-button">
          <FileArrowUp size={13} weight="bold" /> Upload Goods Invoice
          <input ref={addRef} type="file" accept={ACCEPT} className="hidden" disabled={busy}
                 onChange={addInvoice} data-testid="sender-add-invoice-input" />
        </label>
      )}
    >
      {currentInvoices.length === 0 ? (
        <p className="text-sm text-[hsl(var(--muted))]" data-testid="sender-no-invoices">
          {canUpload ? "No Goods Invoice uploaded yet. Use “Upload Goods Invoice” above — this is required before payment."
                     : (paid ? "Goods Invoice is locked after payment." : "No Goods Invoice on this shipment.")}
        </p>
      ) : (
        <div className="space-y-2" data-testid="sender-invoice-list">
          {currentInvoices.map((doc) => {
            const preEwb = !doc.invoice_has_ewb && !doc.invoice_replacement_pending;
            const pending = doc.invoice_replacement_pending;
            const postEwbReplaceable = doc.invoice_has_ewb && !pending;
            return (
              <div key={doc.id} className="flex items-start justify-between gap-3 border border-[hsl(var(--border))] rounded p-2"
                   data-testid={`sender-invoice-${doc.public_document_id}`}>
                <div className="min-w-0">
                  <div className="mono text-sm truncate">{doc.file_name || doc.public_document_id}</div>
                  {pending && (
                    <div className="text-xs text-amber-700 mt-1 inline-flex items-center gap-1"
                         data-testid={`sender-invoice-pending-${doc.public_document_id}`}>
                      Replacement awaiting Accounts review.
                      {singlePending && (
                        <button className="underline" onClick={() => openSigned(singlePending.id)}
                                data-testid={`sender-invoice-view-pending-${doc.public_document_id}`}>
                          View replacement
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 shrink-0">
                  <button className="btn-ghost text-xs" onClick={() => openSigned(doc.id)}
                          data-testid={`sender-invoice-view-${doc.public_document_id}`}>
                    <Eye size={12} weight="bold" /> View
                  </button>
                  {canModify && preEwb && (
                    <>
                      {canUpload && (
                        <label className="btn-ghost cursor-pointer text-xs" data-testid={`sender-invoice-replace-btn-${doc.public_document_id}`}>
                          <ArrowsClockwise size={12} weight="bold" /> Replace
                          <input type="file" accept={ACCEPT} className="hidden" disabled={busy}
                                 ref={(el) => (replaceRefs.current[doc.id] = el)}
                                 onChange={replaceInvoice(doc, false)}
                                 data-testid={`sender-invoice-replace-input-${doc.public_document_id}`} />
                        </label>
                      )}
                      {canDelete && (
                        <button className="btn-ghost text-xs text-[hsl(var(--error))]" disabled={busy}
                                onClick={() => deleteInvoice(doc)}
                                data-testid={`sender-invoice-delete-${doc.public_document_id}`}>
                          <Trash size={12} weight="bold" /> Delete
                        </button>
                      )}
                    </>
                  )}
                  {canUpload && postEwbReplaceable && (
                    <label className="btn-ghost cursor-pointer text-xs" data-testid={`sender-invoice-replace-btn-${doc.public_document_id}`}>
                      <ArrowsClockwise size={12} weight="bold" /> Replace
                      <input type="file" accept={ACCEPT} className="hidden" disabled={busy}
                             onChange={replaceInvoice(doc, true)}
                             data-testid={`sender-invoice-replace-input-${doc.public_document_id}`} />
                    </label>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}
